import React, { useState, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { useSelector } from 'react-redux';
import { addLocale } from 'primereact/api';

import { getOutboxInvoiceList, getOutboxInvoicePdf, getOutboxInvoiceView, getOutboxInvoiceData, cancelEArchiveInvoice } from './PortalRequest.js';
import { getSendingDataDetails, formatAmount, formatDate, HeaderProgram, localeDate } from './utils/utils.js';
import InvoiceViewDialog from './utils/InvoiceViewDialog.js';
import { InvoiceTypes, InvoiceTypeCode, getDateTemplate, getVknNameTemplate, getStatusColor } from './eFatura/utils/utils.js';
import { Dropdown } from 'primereact/dropdown';
import Loading from '../Loading.js';

export default function OutboxInvoiceList() {
    const { selectedCompany } = useSelector((state) => state.userCompany);
    const [invoices, setInvoices] = useState([]);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [invoiceViewDialog, setInvoiceViewDialog] = useState(false);
    const [invoiceView, setInvoiceView] = useState(null);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    const [pending, setPending] = useState(false);
    const [reasonDialog, setReasonDialog] = useState(false);
    const [cancelDate, setCancelDate] = useState(null);
    const [selectedCompanyCode, setSelectedCompanyCode] = useState(null);

    const dt = useRef(null);
    const toast = useRef(null);
    addLocale('tr', localeDate);

    const getReceiptList = async () => {
        setSelectedReceipt(null);
        setLoading(true);
        let req = null;
        let selectedModule = selectedCompany.modules.find((module) => module.title === 'E-Fatura');

        if (selectedModule.setting.settings.program.title === 'Paraşüt E-Fatura') {
            let company = selectedModule.setting.settings.program.parasutCompanies.find((company) => company.id === selectedCompanyCode);
            req = company.webService;
        } else {
            req = selectedModule.setting.settings.webService;
        }

        let invoiceList = null;
        try {
            invoiceList = await getOutboxInvoiceList({
                username: req.requirements.username,
                password: req.requirements.password,
                webServiceUrl: req.url,
                startDate: formatDate(startDate, 'dashReverse'),
                endDate: formatDate(endDate, 'dashReverse')
            });

            if (invoiceList.success === false) {
                setLoading(false);
                toast.current.show({ severity: 'error', summary: 'Hata', detail: invoiceList.message, life: 3000 });
                return;
            } else {
                const copy = [...invoiceList.data.data];
                setInvoices(copy.slice().reverse());
            }
        } catch (error) {
            setLoading(false);
            setTimeout(() => {
                error.code === 'ERR_NETWORK'
                    ? toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İstek zaman aşımına uğradı', life: 3000 })
                    : toast.current.show({ severity: 'error', summary: 'Hata', detail: error?.response?.data?.message || 'Bir hata oluştu', life: 3000 });
            }, 100);
            return;
        }

        setLoading(false);

        setTimeout(() => {
            toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Fatura başarıyla getirildi.', life: 3000 });
        }, 100);
    };

    if (loading) {
        return <Loading loading={loading} onChangeLoading={() => setLoading(false)} />;
    }

    const sendViewReceipts = async (rowData) => {
        const data = rowData.DocumentId;
        const reqData = getSendingDataDetails(selectedCompany, selectedCompanyCode);
        reqData.documentId = data;

        setPending(true);
        try {
            const res = await getOutboxInvoiceView(reqData);

            if (res.data.success === false) {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: res.data.message, life: 3000 });
            } else {
                setInvoiceView(res.data.data.Html);
                setInvoiceViewDialog(true);
            }

            setPending(false);
        } catch (error) {
            setPending(false);
            toast.current.show({ severity: 'error', summary: 'Hata', detail: error?.response?.data?.message || 'Bir hata oluştu', life: 3000 });
        }
    };

    const header = (
        <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
            <div className="flex align-items-center gap-5">
                <h4 className="m-0">Fatura Listesi</h4>
            </div>
            <div>
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Ara..." />
                </span>
            </div>
        </div>
    );

    const rejectReceipts = async () => {
        const data = selectedInvoice;
        const reqData = getSendingDataDetails(selectedCompany, selectedCompanyCode);
        reqData.documentId = data.DocumentId;
        reqData.cancelDate = formatDate(cancelDate, 'dashReverse');

        setReasonDialog(false);

        setPending(true);
        try {
            const res = await cancelEArchiveInvoice(reqData);

            if (res.data.success === false) {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: res.data.message, life: 3000 });
            } else {
                setPending(false);
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Fatura başarıyla iptal edildi.', life: 3000 });
                setTimeout(() => {
                    getReceiptList();
                }, 1500);
            }
        } catch (error) {
            setPending(false);
            toast.current.show({ severity: 'error', summary: 'Hata', detail: error?.response?.data?.message || 'Bir hata oluştu', life: 3000 });
        }

        setSelectedInvoice(null);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="align-items-center flex justify-content-center">
                <Button
                    icon="pi pi-eye"
                    severity="info"
                    className="mr-2"
                    size="large"
                    tooltip="Görüntüle"
                    tooltipOptions={{ position: 'top', mouseTrack: true, mouseTrackTop: 15 }}
                    rounded
                    loading={pending}
                    onClick={() => {
                        setSelectedInvoice(rowData);
                        sendViewReceipts(rowData);
                    }}
                />
                {rowData.StatusCode === 1000 && rowData.TypeCode === 4 && (
                    <>
                        <Button
                            icon="pi pi-times"
                            severity="danger"
                            className="mr-2"
                            size="large"
                            tooltip="Reddet"
                            tooltipOptions={{ position: 'top', mouseTrack: true, mouseTrackTop: 15 }}
                            rounded
                            loading={pending}
                            onClick={() => {
                                setReasonDialog(true);
                                setSelectedInvoice(rowData);
                            }}
                        />
                    </>
                )}
            </div>
        );
    };

    const onCloseInvoiceViewDialog = () => {
        setInvoiceViewDialog(false);
        setInvoiceView(null);
    };

    const onCloseInvoiceDialog = () => {
        setReasonDialog(false);
        setSelectedInvoice(null);
    };

    const scenerioTypes = [
        { id: 0, value: 'KAĞIT', name: 'Kağıt' },
        { id: 1, value: 'ELEKTRONIK', name: 'Elektronik' }
    ];

    const InvoiceStatus = (rowData) => {
        const typeCode = InvoiceTypes.find((type) => type.id === rowData.TypeCode);
        const invoiceTipTypeCode = InvoiceTypeCode.find((type) => type.id === rowData.InvoiceTipTypeCode);
        const scenerioType = scenerioTypes.find((type) => type.id === rowData.ScenarioCode);

        return (
            <div className="text-center">
                <span className={`p-badge text-white mb-3 px-3 h-auto`} style={{ backgroundColor: typeCode.color }}>
                    {typeCode.id === 4 ? typeCode.name + ' / ' + scenerioType.name : typeCode.name}
                </span>
                <br />
                <span className={`p-badge text-white px-3 h-auto`} style={{ backgroundColor: invoiceTipTypeCode.color }}>
                    {invoiceTipTypeCode.name}
                </span>
            </div>
        );
    };

    const getInvoicePdf = async () => {
        const data = selectedInvoice.DocumentId;
        const reqData = getSendingDataDetails(selectedCompany, selectedCompanyCode);
        reqData.documentId = data;

        setPending(true);
        try {
            const res = await getOutboxInvoicePdf(reqData);

            if (res.data.success === false) {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: res.data.message, life: 3000 });
            } else {
                const linkSource = `data:application/pdf;base64,${res.data.data.Data}`;
                const downloadLink = document.createElement('a');
                const fileName = `${selectedInvoice.InvoiceId}.pdf`;

                downloadLink.href = linkSource;
                downloadLink.download = fileName;
                downloadLink.click();
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: error?.response?.data?.message || 'Bir hata oluştu', life: 3000 });
        }

        setPending(false);
    };

    const getInvoiceXML = async () => {
        const data = selectedInvoice.DocumentId;
        const reqData = getSendingDataDetails(selectedCompany, selectedCompanyCode);
        reqData.documentId = data;

        setPending(true);

        try {
            const res = await getOutboxInvoiceData(reqData);

            if (res.data.success === false) {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: res.data.message, life: 3000 });
            } else {
                const linkSource = `data:application/xml;base64,${res.data.data.Data}`;
                const downloadLink = document.createElement('a');
                const fileName = `${selectedInvoice.InvoiceId}.xml`;

                downloadLink.href = linkSource;
                downloadLink.download = fileName;
                downloadLink.click();
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: error?.response?.data?.message || 'Bir hata oluştu', life: 3000 });
        }

        setPending(false);
    };

    const filterParasutCompanies = () => {
        const copy = [];
        selectedCompany.modules
            .filter((module) => module.title === 'E-Fatura')[0]
            .setting.settings.program.parasutCompanies.map((company) => {
                copy.push({ label: company.name.substring(0, 28), value: company.id });
            });
        return copy;
    };

    return (
        <div>
            <Toast ref={toast} />
            <HeaderProgram selectedCompany={selectedCompany} moduleName="E-Fatura" />
            <br />
            <h3 className="mb-5">Fatura Tablosu</h3>
            <div className="card flex align-items-end justify-content-between">
                <div className="flex align-items-end gap-5">
                    {selectedCompany.modules[0].setting.settings.program.title === 'Paraşüt E-Fatura' && (
                        <div className="w-17rem">
                            <label className="mb-2 block">Şirketler</label>
                            <Dropdown
                                dataKey="_id"
                                value={selectedCompanyCode}
                                onChange={(e) => setSelectedCompanyCode(e.value)}
                                options={filterParasutCompanies()}
                                optionLabel="label"
                                placeholder="Şirket Seçiniz"
                                filter
                                className="w-full"
                                emptyMessage="Şirket Bulunamadı"
                            />
                        </div>
                    )}
                    <div className="w-17rem">
                        <label className="block mb-2" htmlFor="cal_date">
                            Başlangıç Tarih
                        </label>
                        <Calendar className="w-full" value={startDate} dateFormat="dd/mm/yy" locale="tr" onChange={(e) => setStartDate(e.value)} showIcon placeholder="Lütfen başlangıç tarihi seçiniz" />
                    </div>
                    <div className="w-17rem">
                        <label className="block mb-2" htmlFor="cal_date">
                            Bitiş Tarih
                        </label>
                        <Calendar className="w-full" value={endDate} dateFormat="dd/mm/yy" locale="tr" onChange={(e) => setEndDate(e.value)} showIcon placeholder="Lütfen bitiş tarihi seçiniz" />
                    </div>
                    <Button icon="pi pi-ticket" onClick={() => getReceiptList()} className="px-4" label="Fatura Getir" disabled={!(startDate && endDate)} />
                </div>
            </div>

            <div className="card">
                <DataTable
                    ref={dt}
                    value={invoices}
                    dataKey="InvoiceId"
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 100, 200]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="{totalRecords} faturadan {first} ila {last} arası gösteriliyor"
                    globalFilter={globalFilter}
                    header={header}
                    emptyMessage="Fatura Bulunamadı"
                    className="p-datatable-gridlines"
                    selectionMode={'checkbox'}
                    selection={selectedReceipt}
                    onSelectionChange={(e) => setSelectedReceipt(e.value)}
                >
                    <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>
                    <Column field="InvoiceId" header="Fatura No" sortable body={(rowData) => <p className="text-pink-500 font-semibold">{rowData.InvoiceId}</p>} style={{ minWidth: '6rem' }}></Column>
                    <Column header="Fatura Tarihi / Oluşturulma Tarihi" body={(rowData) => getDateTemplate(rowData)} style={{ minWidth: '6rem' }}></Column>
                    <Column header="Alıcı VKN-TCKN / Unvan" body={(rowData) => getVknNameTemplate(rowData)} style={{ minWidth: '6rem' }}></Column>
                    <Column field="PayableAmount" header="Ödenecek Tutar" sortable body={(rowData) => formatAmount(rowData.PayableAmount, rowData.DocumentCurrencyCode)} style={{ minWidth: '6rem' }}></Column>
                    <Column field="TaxExclusiveAmount" header="KDV'siz Tutar" sortable body={(rowData) => formatAmount(rowData.TaxExclusiveAmount, rowData.DocumentCurrencyCode)} style={{ minWidth: '6rem' }}></Column>
                    <Column field="TaxTotal" header="Toplam KDV" sortable body={(rowData) => formatAmount(rowData.TaxTotal, rowData.DocumentCurrencyCode)} style={{ minWidth: '6rem' }}></Column>
                    <Column header="Senaryo Tipi / Fatura Tipi" body={(rowData) => InvoiceStatus(rowData)} style={{ minWidth: '12rem' }}></Column>
                    <Column field="StatusCode" header="Fatura Durumu" sortable body={(rowData) => getStatusColor(rowData)} style={{ minWidth: '6rem' }}></Column>
                    <Column header="İşlemler" body={actionBodyTemplate} className="text-center" exportable={false} style={{ minWidth: '5rem' }}></Column>
                </DataTable>
            </div>

            <InvoiceViewDialog invoiceVisible={invoiceViewDialog} invoiceView={invoiceView} onDownloadPDF={() => getInvoicePdf()} onDownloadXML={() => getInvoiceXML()} onCloseDialog={onCloseInvoiceViewDialog} />

            <Dialog header="İptal Etme" visible={reasonDialog} style={{ width: '30vw' }} modal onHide={() => onCloseInvoiceDialog()}>
                <div className="py-3">
                    <div className="mb-2">
                        <label>İptal Tarihi</label>
                    </div>
                    <div className="mb-2">
                        <Calendar className="w-full" value={cancelDate} dateFormat="dd/mm/yy" locale="tr" onChange={(e) => setCancelDate(e.value)} showIcon placeholder="Lütfen iptal tarihi seçiniz." />
                    </div>
                    <div className="justify-content-end flex mt-5">
                        <Button icon="pi pi-cross" label="Onayla" onClick={() => rejectReceipts()} disabled={!cancelDate} />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
